/**
 * Focus handling for the dropdown that antd opens from a column filter trigger.
 *
 * The dropdown lives in a portal at the end of the document, so focus has to be moved in and out by
 * hand, and rc-dropdown swallows Tab while it is open.
 */

const DROPDOWN_OPEN_CLASS = 'ant-dropdown-open';
const DROPDOWN_HIDDEN_CLASS = 'ant-dropdown-hidden';
const TAB_BOUND_FLAG = 'filterTabBound';
const POPUP_READY_DELAY = 100;

export const filterPopupClassName = (columnKey: string) => `filter-dropdown-${columnKey}`;

let openedBy: HTMLButtonElement | null = null;

export const rememberFilterTrigger = (trigger: HTMLButtonElement) => {
  openedBy = trigger;
};

const isTabbable = (element: HTMLElement) => element.tabIndex >= 0 && !element.hasAttribute('disabled');

const exitPoint = (popup: HTMLElement, backwards: boolean) => {
  const tabbable = Array.from(popup.querySelectorAll<HTMLElement>('*')).filter(isTabbable);

  return backwards ? tabbable[0] : tabbable[tabbable.length - 1];
};

const closePopup = () => {
  openedBy?.focus();
  openedBy?.click();
};

const keepTabInsidePopup = (popup: HTMLElement) => {
  if (popup.dataset[TAB_BOUND_FLAG]) return;

  popup.dataset[TAB_BOUND_FLAG] = 'true';
  popup.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;

    event.stopPropagation();

    if (document.activeElement !== exitPoint(popup, event.shiftKey)) return;

    event.preventDefault();
    closePopup();
  });
};

type FilterPopupOptions = {
  rootClassName: string;
  focusSelector: string;
};

export const focusFilterPopup = ({ rootClassName, focusSelector }: FilterPopupOptions) => {
  const trigger = openedBy;

  setTimeout(() => {
    const popup = document.querySelector<HTMLElement>(`.${rootClassName}:not(.${DROPDOWN_HIDDEN_CLASS})`);

    if (!popup || trigger !== openedBy || !trigger?.classList.contains(DROPDOWN_OPEN_CLASS)) return;

    keepTabInsidePopup(popup);
    popup.querySelector<HTMLElement>(focusSelector)?.focus();
  }, POPUP_READY_DELAY);
};

export const restoreFilterTriggerFocus = ({ rootClassName }: { rootClassName: string }) => {
  const popup = document.activeElement?.closest('.ant-dropdown');
  const trigger = openedBy;

  if (!popup?.classList.contains(rootClassName)) return;

  setTimeout(() => {
    const active = document.activeElement;
    const focusIsLost = active === document.body || popup.contains(active);

    if (focusIsLost && trigger === openedBy) trigger?.focus();
  });
};
